// Modern Portfolio App - Vanilla JavaScript
// Theme, Language, Scroll Effects
import '../css/style.css';

// ==========================================================================
// Translations
// ==========================================================================
import { translations } from './translations.js';
import CyberBackground from './cyber-background.js';
import PortfolioFilter from './portfolio-filter.js';

// ==========================================================================
// App State
// ==========================================================================
let currentLang = 'pl';
let currentTheme = 'light';

// Safe localStorage access with error handling
try {
  currentLang = localStorage.getItem('lang') || 'pl';
  currentTheme = localStorage.getItem('theme') || 'light';
} catch (e) {
  console.warn('localStorage not available:', e);
}



// ==========================================================================
// Theme Management
// ==========================================================================
function initTheme() {
  // Check for saved theme or use system preference
  if (!localStorage.getItem('theme')) {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    currentTheme = prefersDark ? 'dark' : 'light';
  }
  applyTheme(currentTheme);
}

function applyTheme(theme) {
  currentTheme = theme;
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem('theme', theme);
  } catch (e) {
    console.warn('Cannot save theme:', e);
  }
}

function toggleTheme() {
  const newTheme = currentTheme === 'light' ? 'dark' : 'light';
  applyTheme(newTheme);
}

// ==========================================================================
// Language Management
// ==========================================================================
function initLanguage() {
  applyLanguage(currentLang);
}

function applyLanguage(lang) {
  currentLang = lang;
  try {
    localStorage.setItem('lang', lang);
  } catch (e) {
    console.warn('Cannot save language:', e);
  }

  // Update all elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach(element => {
    const key = element.getAttribute('data-i18n');
    if (translations[lang] && translations[lang][key]) {
      element.textContent = translations[lang][key];
    }
  });

  // Update Dropdown UI
  const langBtn = document.querySelector('.lang-btn');
  if (langBtn) {
    const flag = lang === 'pl' ? '🇵🇱' : '🇬🇧';
    const code = lang === 'pl' ? 'PL' : 'EN';
    langBtn.querySelector('.lang-flag').textContent = flag;
    langBtn.querySelector('.lang-code').textContent = code;
  }

  // Update active option
  document.querySelectorAll('.lang-option').forEach(opt => {
    opt.classList.toggle('active', opt.dataset.lang === lang);
  });

  // Update HTML lang attribute
  document.documentElement.setAttribute('lang', lang);

  // Update meta description
  updateMetaDescription(lang);
}

function updateMetaDescription(lang) {
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) {
    if (lang === 'pl') {
      metaDesc.setAttribute('content', 'Senior Backend / Full-Stack Developer z 9+ latami doświadczenia. Specjalizacja: PHP, .NET Core, Flutter.');
    } else {
      metaDesc.setAttribute('content', 'Senior Backend / Full-Stack Developer with 9+ years of experience. Specialization: PHP, .NET Core, Flutter.');
    }
  }
}

function initLanguageSelector() {
  const dropdown = document.getElementById('lang-dropdown');
  const btn = dropdown?.querySelector('.lang-btn');
  const options = dropdown?.querySelectorAll('.lang-option');

  if (!dropdown || !btn) return;

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', !isExpanded);
  });

  options.forEach(opt => {
    opt.addEventListener('click', () => {
      const lang = opt.dataset.lang;
      applyLanguage(lang);
      btn.setAttribute('aria-expanded', 'false');
    });
  });

  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target)) {
      btn.setAttribute('aria-expanded', 'false');
    }
  });
}

// ==========================================================================
// Scroll Effects
// ==========================================================================
function initScrollEffects() {
  const header = document.getElementById('header');

  // Header scroll effect
  let lastScroll = 0;

  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;

    if (currentScroll > 100) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }

    lastScroll = currentScroll;
  });

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const targetId = this.getAttribute('href');

      if (targetId === '#') return;

      const targetElement = document.querySelector(targetId);
      if (targetElement) {
        const headerHeight = header.offsetHeight;
        const targetPosition = targetElement.offsetTop - headerHeight - 20;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  });

  // Initialize scroll-based animations
  initScrollAnimations();

  // Initialize parallax effects
  initParallaxEffects();
}

// ==========================================================================
// Scroll-Based Animations with Intersection Observer
// ==========================================================================
function initScrollAnimations() {
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // FIRST: Auto-add stagger delays to grid items (before observing)
  autoStaggerDelays('.case-studies-grid .case-study-card');
  autoStaggerDelays('.services-grid .service-card');
  autoStaggerDelays('.workflow-timeline .workflow-step');
  autoStaggerDelays('.expertise-grid .expertise-card');
  autoStaggerDelays('.stats-grid .stat-card');

  if (prefersReducedMotion) {
    // Make all elements visible immediately without animation
    document.querySelectorAll('.scroll-animate').forEach(el => {
      el.classList.add('visible');
    });
    return;
  }

  // Observer for scroll animations
  const scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        // Unobserve after animation (once mode)
        scrollObserver.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.05, // Lower threshold - trigger earlier
    rootMargin: '50px 0px -50px 0px' // More forgiving margins
  });

  // THEN: Observe all scroll-animate elements (after classes were added)
  document.querySelectorAll('.scroll-animate').forEach(el => {
    scrollObserver.observe(el);
  });
}

// Helper: Auto-apply stagger delays to child elements
function autoStaggerDelays(selector) {
  const items = document.querySelectorAll(selector);
  items.forEach((item, index) => {
    // Add scroll-animate class if not present
    if (!item.classList.contains('scroll-animate')) {
      item.classList.add('scroll-animate', 'fade-up');
    }
    // Set stagger delay via data attribute
    item.setAttribute('data-delay', Math.min(index + 1, 6));
  });
}

// ==========================================================================
// Parallax Effects (subtle, performance-optimized)
// ==========================================================================
function initParallaxEffects() {
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  const hero = document.querySelector('.hero');
  const expertiseBg = document.querySelector('.expertise-bg');

  // Enable parallax on these elements
  if (hero) hero.classList.add('parallax-enabled');
  if (expertiseBg) expertiseBg.classList.add('parallax-enabled');

  let ticking = false;

  const updateParallax = () => {
    const scrollY = window.pageYOffset;

    // Hero parallax (subtle upward movement)
    if (hero && hero.classList.contains('parallax-enabled')) {
      const heroContent = hero.querySelector('.hero-content');
      if (heroContent) {
        const heroOffset = scrollY * 0.15; // 15% of scroll
        heroContent.style.transform = `translateY(${heroOffset}px)`;
      }
    }

    // Expertise background parallax
    if (expertiseBg && expertiseBg.classList.contains('parallax-enabled')) {
      const sectionTop = expertiseBg.parentElement.offsetTop;
      const sectionHeight = expertiseBg.parentElement.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Only apply when section is in view
      if (scrollY + viewportHeight > sectionTop && scrollY < sectionTop + sectionHeight) {
        const relativeScroll = scrollY - sectionTop + viewportHeight;
        const parallaxOffset = (relativeScroll * 0.05); // 5% parallax

        const gradient = expertiseBg.querySelector('.expertise-bg-gradient');
        const particles = expertiseBg.querySelector('.expertise-bg-particles');

        if (gradient) {
          gradient.style.transform = `translateY(${parallaxOffset * 0.5}px)`;
        }
        if (particles) {
          particles.style.transform = `translateY(${parallaxOffset * -0.3}px)`;
        }
      }
    }

    ticking = false;
  };

  // Throttled scroll event using requestAnimationFrame
  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
}

// ==========================================================================
// Toggle Buttons Initialization
// ==========================================================================
function initToggleButtons() {
  const themeBtn = document.getElementById('theme-toggle');

  if (themeBtn) {
    themeBtn.addEventListener('click', toggleTheme);
  }
}

// ==========================================================================
// Mouse Tracking for Expertise Cards Glow Effect
// ==========================================================================
function initMouseTracking() {
  const expertiseCards = document.querySelectorAll('.expertise-card');

  expertiseCards.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      card.style.setProperty('--mouse-x', `${x}%`);
      card.style.setProperty('--mouse-y', `${y}%`);
    });

    card.addEventListener('mouseleave', () => {
      card.style.setProperty('--mouse-x', '50%');
      card.style.setProperty('--mouse-y', '50%');
    });
  });
}

// ==========================================================================
// Animated Counter for Stats Cards
// ==========================================================================
function initCounterAnimation() {
  const observerOptions = {
    threshold: 0.5,
    rootMargin: '0px'
  };

  const animateCounter = (element) => {
    const target = parseInt(element.getAttribute('data-count'));
    const duration = 2000; // 2 seconds
    const increment = target / (duration / 16); // 60fps
    let current = 0;

    const updateCounter = () => {
      current += increment;

      if (current < target) {
        // Format based on the original text
        const originalText = element.textContent;
        if (originalText.includes('K')) {
          element.textContent = Math.floor(current) + 'K+';
        } else {
          element.textContent = Math.floor(current) + '+';
        }
        requestAnimationFrame(updateCounter);
      } else {
        // Set final value
        const originalText = element.getAttribute('data-i18n');
        const lang = currentLang || 'pl';
        if (translations[lang] && translations[lang][originalText]) {
          element.textContent = translations[lang][originalText];
        }
      }
    };

    requestAnimationFrame(updateCounter);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
        entry.target.classList.add('counted');
        animateCounter(entry.target);
      }
    });
  }, observerOptions);

  // Observe all stat numbers
  document.querySelectorAll('.stat-number[data-count]').forEach(el => {
    observer.observe(el);
  });
}

// ==========================================================================
// Tech Slider Logic
// ==========================================================================
function initTechSlider() {
  const slides = document.querySelectorAll('.tech-slide');
  const dotsContainer = document.querySelector('.slider-dots');
  const prevBtn = document.querySelector('.slider-btn.prev');
  const nextBtn = document.querySelector('.slider-btn.next');
  let currentSlide = 0;
  let slideInterval;

  if (!slides.length) return;

  // Create dots
  dotsContainer.innerHTML = '';
  slides.forEach((_, index) => {
    const dot = document.createElement('div');
    dot.classList.add('slider-dot');
    if (index === 0) dot.classList.add('active');
    dot.addEventListener('click', () => {
      goToSlide(index);
      resetInterval();
    });
    dotsContainer.appendChild(dot);
  });

  const dots = document.querySelectorAll('.slider-dot');

  function goToSlide(index) {
    slides[currentSlide].classList.remove('active');
    dots[currentSlide].classList.remove('active');

    currentSlide = (index + slides.length) % slides.length;

    slides[currentSlide].classList.add('active');
    dots[currentSlide].classList.add('active');
  }

  function nextSlide() {
    goToSlide(currentSlide + 1);
  }

  function prevSlide() {
    goToSlide(currentSlide - 1);
  }

  if (prevBtn) prevBtn.addEventListener('click', () => {
    prevSlide();
    resetInterval();
  });

  if (nextBtn) nextBtn.addEventListener('click', () => {
    nextSlide();
    resetInterval();
  });

  function resetInterval() {
    if (slideInterval) clearInterval(slideInterval);
    slideInterval = setInterval(nextSlide, 5000);
  }

  // Initialize auto-play
  resetInterval();

  // Pause on hover
  const sliderContainer = document.querySelector('.tech-slider-wrapper');
  if (sliderContainer) {
    sliderContainer.addEventListener('mouseenter', () => clearInterval(slideInterval));
    sliderContainer.addEventListener('mouseleave', resetInterval);
  }
}

// ==========================================================================
// Floating Menu Logic
// ==========================================================================
function initFloatingMenu() {
  const links = document.querySelectorAll('.floating-link');
  const sections = document.querySelectorAll('section, header#hero');

  // Update active state on scroll
  const observerOptions = {
    threshold: 0.3, // Section must be 30% visible
    rootMargin: "-100px 0px -100px 0px" // Offset for header
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Remove active from all
        links.forEach(link => link.classList.remove('active'));

        // Add active to current
        const id = entry.target.getAttribute('id');
        const activeLink = document.querySelector(`.floating-link[href="#${id}"]`);
        if (activeLink) {
          activeLink.classList.add('active');
        }
      }
    });
  }, observerOptions);

  sections.forEach(section => {
    observer.observe(section);
  });
}
// ==========================================================================
// Contact Form Logic
// ==========================================================================
const initContactForm = () => {
  const form = document.getElementById('contact-form');
  if (!form) return;

  const submitBtn = form.querySelector('.submit-btn');
  const messageContainer = document.getElementById('form-message');

  // Validation regex patterns
  const patterns = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    name: /.{2,}/,
    subject: /.{3,}/,
    message: /.{10,}/
  };

  // Field validation function
  const validateField = (field) => {
    const errorKey = field.dataset.errorKey;
    const errorElement = field.nextElementSibling;
    const value = field.value.trim();
    let isValid = true;

    // Check if required field is empty
    if (field.hasAttribute('required') && !value) {
      isValid = false;
    }
    // Check specific patterns
    else if (field.name === 'email' && !patterns.email.test(value)) {
      isValid = false;
    }
    else if (field.name === 'name' && !patterns.name.test(value)) {
      isValid = false;
    }
    else if (field.name === 'message' && !patterns.message.test(value)) {
      isValid = false;
    }

    // Toggle error state
    if (!isValid) {
      field.parentElement.classList.add('has-error');
      // Update error text from translations if available
      if (window.translations && currentLang && window.translations[currentLang]) {
        // Need to traverse the object path string (e.g. "contact.form.errors.email")
        const keys = errorKey.split('.');
        let translation = window.translations[currentLang];
        for (const key of keys) {
          translation = translation ? translation[key] : null;
        }
        if (translation) errorElement.textContent = translation;
      }
    } else {
      field.parentElement.classList.remove('has-error');
    }

    return isValid;
  };

  // Add event listeners for real-time validation
  form.querySelectorAll('.form-input').forEach(input => {
    input.addEventListener('blur', () => validateField(input));
    input.addEventListener('input', () => {
      if (input.parentElement.classList.contains('has-error')) {
        validateField(input);
      }
    });
  });

  // Handle form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Check honeypot
    const honeypot = form.querySelector('input[name="website"]');
    if (honeypot && honeypot.value) {
      return; // Generic spam protection
    }

    // Validate all fields
    let sizeValid = true;
    form.querySelectorAll('.form-input').forEach(input => {
      if (!validateField(input)) sizeValid = false;
    });

    if (!sizeValid) return;

    // Set loading state
    submitBtn.classList.add('loading');
    messageContainer.classList.remove('show', 'success', 'error');

    // Get form data
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    try {
      // Simulate network request if no backend (dev mode) or send real request
      const response = await fetch('api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Success
        form.reset();
        messageContainer.classList.add('success', 'show');

        // Get success message translation
        let successMsg = "Wiadomość wysłana! Dziękuję za kontakt.";
        if (currentLang === 'en') successMsg = "Message sent! Thank you for contacting me.";

        if (window.translations && currentLang) {
          successMsg = window.translations[currentLang].contact.form.success;
        }
        messageContainer.textContent = successMsg;

        // Hide success message after 5 seconds
        setTimeout(() => {
          messageContainer.classList.remove('show');
        }, 5000);
      } else {
        throw new Error(result.message || 'Server error');
      }
    } catch (error) {
      // Error handling
      console.error('Form submission error:', error);

      // Check if we are in dev environment (localhost) without PHP
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isDev && !document.querySelector('body[data-php-enabled="true"]')) {
        // Fallback for demo/dev purposes
        setTimeout(() => {
          form.reset();
          submitBtn.classList.remove('loading');
          messageContainer.classList.add('success', 'show');
          messageContainer.textContent = "[DEV MODE] Backend nie jest podłączony, ale frontend działa poprawnie!";
        }, 1500);
        return;
      }

      messageContainer.classList.add('error', 'show');

      // Get error message translation
      let errorMsg = "Wystąpił błąd. Spróbuj ponownie.";
      if (currentLang === 'en') errorMsg = "An error occurred. Please try again.";

      if (window.translations && currentLang) {
        errorMsg = window.translations[currentLang].contact.form.error;
      }
      messageContainer.textContent = errorMsg;
    } finally {
      if (!window.location.hostname.includes('localhost')) {
        submitBtn.classList.remove('loading');
      }
    }
  });
};

// Initialize mouse tracking after DOM is ready
// Initialize app components
const initApp = () => {
  initTheme();
  initLanguage();
  initLanguageSelector();
  initScrollEffects();
  initToggleButtons();
  initMouseTracking();
  initCounterAnimation();
  initTechSlider();
  initFloatingMenu();
  initContactForm();
  new CyberBackground();
  new PortfolioFilter();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
